// 地图页的 DOM 叠层：热点上方的提示（teaser）、已点亮计数、一次性提示条、终章故事。

/** kind = 'chapter'：章节标题（塔罗），贴在该章第一颗星旁，同样参与避让 */
export type TeaserLabel = { id: string; text: string; x: number; y: number; r: number; kind?: 'chapter'; locked?: boolean };
/** 地图上的星盘按钮（章节完成后出现在这一章星座的末尾）；state solved = 已解开（点开重读那一页） */
export type DialMarker = { chapter: number; x: number; y: number; state: 'ready' | 'solved' };
/** 标签不能压住的圆（所有地点的热点，屏幕 CSS px） */
export type Obstacle = { x: number; y: number; r: number };

type Rect = { l: number; t: number; r: number; b: number };
const GAP = 6;
const MARGIN = 8;

function rectCircleOverlap(a: Rect, c: Obstacle): number {
  // 圆按外接方形算重叠面积：足够当惩罚项，又便宜
  const l = Math.max(a.l, c.x - c.r);
  const r = Math.min(a.r, c.x + c.r);
  const t = Math.max(a.t, c.y - c.r);
  const b = Math.min(a.b, c.y + c.r);
  return r > l && b > t ? (r - l) * (b - t) : 0;
}

function rectOverlap(a: Rect, b: Rect): number {
  const w = Math.min(a.r, b.r) - Math.max(a.l, b.l);
  const h = Math.min(a.b, b.b) - Math.max(a.t, b.t);
  return w > 0 && h > 0 ? w * h : 0;
}

function permutations<T>(xs: T[]): T[][] {
  if (xs.length <= 1) return [xs];
  return xs.flatMap((x, i) => permutations([...xs.slice(0, i), ...xs.slice(i + 1)]).map((p) => [x, ...p]));
}

/** 候选位置：正上优先，然后绕热点一圈（16 个方向）× 由近到远 6 圈（推远的有牵引线）；标签矩形贴在该方向上离热点 d 处 */
const RINGS = 6;
const DIRS = 16;
function candidates(l: TeaserLabel, w: number, h: number): Rect[] {
  const out: Rect[] = [];
  // 章节标题：沿锚点所在的水平线（两章之间虚线的中点）左右滑动找空位，从中点向两边；都撞再绕圈找
  if (l.kind === 'chapter') {
    for (let k = 0; k <= 12; k++) {
      const dx = Math.ceil(k / 2) * 24 * (k % 2 ? 1 : -1);
      for (const dy of [0, -8, 8]) {
        const cx = l.x + dx;
        const cy = l.y + dy;
        out.push({ l: cx - w / 2, t: cy - h / 2, r: cx + w / 2, b: cy + h / 2 });
      }
    }
  }
  for (let k = 0; k < RINGS; k++) {
    const d = l.r + GAP + k * 20;
    for (let i = 0; i < DIRS; i++) {
      // i = 0 正上，依次左右交替展开
      const step = Math.ceil(i / 2) * (i % 2 ? 1 : -1);
      const a = -Math.PI / 2 + (step * 2 * Math.PI) / DIRS;
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      // 矩形中心沿方向推到"矩形边缘离圆心 d"处
      const ext = Math.abs(ux) * (w / 2) + Math.abs(uy) * (h / 2);
      const cx = l.x + ux * (d + ext);
      const cy = l.y + uy * (d + ext);
      out.push({ l: cx - w / 2, t: cy - h / 2, r: cx + w / 2, b: cy + h / 2 });
    }
  }
  return out;
}

/** 星盘按钮的图案：外圈刻度 + 内圈 + 中间一颗四芒星（金线，代码绘制） */
const DIAL_SVG = (() => {
  let ticks = '';
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    const r0 = i % 3 === 0 ? 15.5 : 17;
    ticks += `M${(24 + Math.cos(a) * r0).toFixed(2)} ${(24 + Math.sin(a) * r0).toFixed(2)}L${(24 + Math.cos(a) * 19.5).toFixed(2)} ${(24 + Math.sin(a) * 19.5).toFixed(2)}`;
  }
  return `<svg viewBox="0 0 48 48" class="map-dial-icon" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="24" cy="24" r="21.5"/><circle cx="24" cy="24" r="12"/><path d="${ticks}"/><path d="M24 15.5l2 6.5 6.5 2-6.5 2-2 6.5-2-6.5-6.5-2 6.5-2z" fill="currentColor"/></svg>`;
})();

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => {
  const e = document.querySelector<T>(sel);
  if (!e) throw new Error(`Missing ${sel}`);
  return e;
};

export class MapUi {
  private readonly labels = $('#map-labels');
  private readonly lit = $('#map-lit');
  private readonly toastEl = $('#toast');
  private readonly ending = $('#ending-story');
  private readonly dialLayer = $('#map-dials');
  private readonly dialEls = new Map<number, HTMLButtonElement>();
  onDial: ((chapter: number) => void) | null = null;
  private readonly nodes = new Map<string, HTMLElement>();
  private readonly leaders = new Map<string, HTMLElement>();
  /** 上一帧每个标签选中的候选位置：仍然不撞时沿用，倾斜时标签不来回跳 */
  private readonly chosen = new Map<string, number>();
  /** 最近一次布局结果（测试读取） */
  lastLayout: { id: string; rect: Rect; overlap: number }[] = [];
  private toastTimer = 0;

  /**
   * 每帧调用：标签贴在热点旁（屏幕坐标由地图场景投影给出）。
   * 简单避让：按顺序给每个标签挑一个候选位置，不压住任何热点（obstacles）、不压住已放好的标签、
   * 不出可用区域（bounds：头部之下、底栏之上）；都撞时取重叠最少的。
   */
  setTeasers(list: TeaserLabel[], obstacles: Obstacle[] = [], bounds?: { top: number; bottom: number }): void {
    const keep = new Set(list.map((l) => l.id));
    for (const [id, node] of this.nodes) {
      if (!keep.has(id)) {
        node.remove();
        this.nodes.delete(id);
        this.leaders.get(id)?.remove();
        this.leaders.delete(id);
      }
    }
    const vw = window.innerWidth;
    const top = (bounds?.top ?? 0) + MARGIN;
    const bottom = (bounds?.bottom ?? window.innerHeight) - MARGIN;
    // 1. 建节点、量尺寸、列候选
    const items = list.map((l) => {
      let node = this.nodes.get(l.id);
      if (!node) {
        node = document.createElement('div');
        node.className = l.kind === 'chapter' ? 'map-teaser map-chapter' : 'map-teaser';
        node.dataset.place = l.id;
        node.textContent = l.text;
        this.labels.append(node);
        this.nodes.set(l.id, node);
      }
      if (node.textContent !== l.text) node.textContent = l.text;
      node.classList.toggle('is-locked', !!l.locked);
      // 实际渲染尺寸可能带小数（行高 1.3 × 12px）：向上取整，避免相邻标签差零点几像素叠在一起
      const box = node.getBoundingClientRect();
      const w = Math.ceil(box.width);
      const h = Math.ceil(box.height);
      // 出界的候选直接夹回屏幕内再评估（与最终摆放一致）
      const cands = candidates(l, w, h).map((c) => {
        const left = Math.max(MARGIN, Math.min(vw - MARGIN - w, c.l));
        const tp = Math.max(top, Math.min(bottom - h, c.t));
        return { l: left, t: tp, r: left + w, b: tp + h };
      });
      // 章节标题只需要别盖住星星本身（标签层不接收点按，点按照样落到热点上）：障碍圆缩到 45%
      const obs = l.kind === 'chapter' ? obstacles.map((ob) => ({ ...ob, r: ob.r * 0.45 })) : obstacles;
      const fixed = cands.map((c) => obs.reduce((o, ob) => o + rectCircleOverlap(c, ob), 0));
      return { l, node, cands, fixed };
    });
    type Item = (typeof items)[number];
    const cost = (it: Item, i: number, placed: Rect[]) => placed.reduce((o, p) => o + rectOverlap(it.cands[i], p), it.fixed[i]);
    /** 按给定顺序贪心摆放，返回每个标签的候选下标和总代价（近处/上方优先作为微小的平局偏好） */
    const greedy = (order: Item[]): { pick: Map<Item, number>; total: number } => {
      const placed: Rect[] = [];
      const pick = new Map<Item, number>();
      let total = 0;
      for (const it of order) {
        let best = 0;
        let bestCost = Infinity;
        for (let i = 0; i < it.cands.length; i++) {
          const c = cost(it, i, placed) + i * 0.01;
          if (c < bestCost) {
            best = i;
            bestCost = c;
          }
          if (c === 0) break;
        }
        pick.set(it, best);
        placed.push(it.cands[best]);
        total += bestCost;
      }
      return { pick, total };
    };
    // 2. 上一帧的位置仍然全部不撞：沿用（倾斜时标签不来回跳）
    let pick = new Map<Item, number>();
    let ok = true;
    const placed: Rect[] = [];
    for (const it of items) {
      const i = this.chosen.get(it.l.id);
      if (i === undefined || i >= it.cands.length || cost(it, i, placed) > 0) {
        ok = false;
        break;
      }
      pick.set(it, i);
      placed.push(it.cands[i]);
    }
    // 3. 否则重新摆：同时解锁的标签很少（通常 3 个），试遍摆放顺序取总代价最小的；多了就按原顺序贪心
    if (!ok) {
      const orders = items.length <= 5 ? permutations(items) : [items];
      let bestTotal = Infinity;
      for (const order of orders) {
        const r = greedy(order);
        if (r.total < bestTotal) {
          bestTotal = r.total;
          pick = r.pick;
        }
        if (bestTotal < 1) break;
      }
    }
    const layout: typeof this.lastLayout = [];
    for (const it of items) {
      const i = pick.get(it) ?? 0;
      this.chosen.set(it.l.id, i);
      const rect = it.cands[i];
      layout.push({ id: it.l.id, rect, overlap: it.fixed[i] });
      it.node.style.transform = `translate(${rect.l.toFixed(1)}px, ${rect.t.toFixed(1)}px)`;
      this.placeLeader(it.l, rect);
    }
    this.lastLayout = layout;
    for (const id of [...this.chosen.keys()]) if (!keep.has(id)) this.chosen.delete(id);
  }

  /** 标签离热点边缘超过 10px 时画一根牵引线：热点边缘 → 标签上最近的点 */
  private placeLeader(l: TeaserLabel, rect: Rect): void {
    const nx = Math.max(rect.l, Math.min(l.x, rect.r));
    const ny = Math.max(rect.t, Math.min(l.y, rect.b));
    const dx = nx - l.x;
    const dy = ny - l.y;
    const dist = Math.hypot(dx, dy);
    let line = this.leaders.get(l.id);
    if (dist - l.r <= 10) {
      if (line) line.hidden = true;
      return;
    }
    if (!line) {
      line = document.createElement('div');
      line.className = 'map-teaser-leader';
      line.dataset.place = l.id;
      this.labels.prepend(line);
      this.leaders.set(l.id, line);
    }
    line.hidden = false;
    const sx = l.x + (dx / dist) * (l.r - 4);
    const sy = l.y + (dy / dist) * (l.r - 4);
    const len = dist - l.r + 6;
    line.style.width = `${len.toFixed(1)}px`;
    line.style.transform = `translate(${sx.toFixed(1)}px, ${(sy - 1).toFixed(1)}px) rotate(${Math.atan2(dy, dx).toFixed(4)}rad)`;
  }

  /** 每帧调用：星盘按钮跟着地图倾斜移动（屏幕 CSS px，按钮中心） */
  setDials(list: DialMarker[]): void {
    const keep = new Set(list.map((d) => d.chapter));
    for (const [k, b] of this.dialEls) {
      if (!keep.has(k)) {
        b.remove();
        this.dialEls.delete(k);
      }
    }
    for (const d of list) {
      let b = this.dialEls.get(d.chapter);
      if (!b) {
        b = document.createElement('button');
        b.type = 'button';
        b.className = 'map-dial';
        b.dataset.chapter = String(d.chapter);
        b.innerHTML = `${DIAL_SVG}<span class="map-dial-cap">星盘</span>`;
        const ch = d.chapter;
        b.onclick = () => this.onDial?.(ch);
        this.dialLayer.append(b);
        this.dialEls.set(d.chapter, b);
      }
      b.classList.toggle('is-solved', d.state === 'solved');
      b.setAttribute('aria-label', d.state === 'solved' ? `第${d.chapter + 1}章的星盘（已解开）` : `第${d.chapter + 1}章的星盘`);
      b.style.transform = `translate(${(d.x - 26).toFixed(1)}px, ${(d.y - 26).toFixed(1)}px)`;
    }
  }

  setLit(done: number, total: number): void {
    const t = `${done}/${total}`;
    if (this.lit.textContent !== t) this.lit.textContent = t;
  }

  toast(text: string, ms = 3200): void {
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (this.toastEl.hidden = true), ms);
  }

  showEnding(text: string, onClose: () => void): void {
    $('#ending-text').textContent = text;
    this.ending.hidden = false;
    $('#ending-ok').onclick = () => {
      this.ending.hidden = true;
      onClose();
    };
  }

  get endingShown(): boolean {
    return !this.ending.hidden;
  }

  hideEnding(): void {
    this.ending.hidden = true;
  }
}
